/**
 * AppShell – Main layout wrapper
 * - Desktop: persistent sidebar (collapsible)
 * - Mobile/Tablet: drawer sidebar via overlay
 *
 * FIX v2: Inject navigate function vào authNavigateRef
 * FIX v3: Wrap với AdminRolePreviewProvider để share previewRole toàn app
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { useAuth, authNavigateRef } from '@/core/auth/AuthContext';
import { AdminRolePreviewProvider } from '@/core/roles/AdminRolePreviewContext';
import { ProjectProvider } from '@/core/projects/ProjectContext';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import { AIProvider } from '@/core/ai/AIContext';
import { GlobalAIChatDrawer } from '@/core/ai/GlobalAIChatDrawer';
import { AIFloatingButton } from '@/core/ai/AIFloatingButton';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar_collapsed') === 'true';
  });

  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { isImpersonating } = useAuth();

  const isAdmin = ['admin', 'super_admin'].includes(user?.roleCode ?? '');

  useEffect(() => {
    authNavigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches) setMobileOpen(false);
    };

    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return (
    <AdminRolePreviewProvider isAdmin={isAdmin}>
      <ProjectProvider>
        <AIProvider>
          <div className="flex min-h-screen bg-slate-50 font-sans text-slate-900">
            <Sidebar
              collapsed={sidebarCollapsed}
              onToggle={() => setSidebarCollapsed(prev => !prev)}
              mobileOpen={mobileOpen}
              onMobileClose={() => setMobileOpen(false)}
            />

            <div className="flex-1 flex flex-col min-w-0">
              <Topbar
                sidebarCollapsed={sidebarCollapsed}
                onMobileMenuToggle={() => setMobileOpen(prev => !prev)}
              />

              <main
                className={`
                flex-1 transition-all duration-300
                ${isImpersonating ? 'pt-[50px]' : ''}
              `}
              >
                <div className="w-full flex-1 min-w-0 p-4 md:p-6 lg:px-8 lg:pb-8 lg:pt-5">
                  {children}
                </div>
              </main>
            </div>

            <GlobalAIChatDrawer />
            <AIFloatingButton />
          </div>
        </AIProvider>
      </ProjectProvider>
    </AdminRolePreviewProvider>
  );
}
